import { c as _c } from "react/compiler-runtime";
import { Text } from "@flat-ui";
import { Box } from "@mui-wrapper";
import { MarkupDueDate } from "@pages/project-details/project-markups/due-date/markup-due-date";
import { MarkupAssignee } from "@pages/project-details/project-markups/markup-assignee";
import { MarkupDescription } from "@pages/project-details/project-markups/sidepanel/markup-description";
import { MarkupPosition } from "@pages/project-details/project-markups/sidepanel/markup-position";
import { MarkupMeasurements } from "@pages/project-details/project-markups/sidepanel/markup-measurements";
import { MarkupSidePanelActivity } from "@pages/project-details/project-markups/sidepanel/markup-sidepanel-activity";
import { MarkupStatusDropdown } from "@pages/project-details/project-markups/status/markup-status-dropdown";
import { userSettingsSelector } from "@store/settings/settings-selector";
import { useAppSelector } from "@store/store-helper";
/** The sections bring their own top margin, which the first one of a column does not need */
const COLUMN_SX = {
  minWidth: 0,
  "& > :first-child": {
    mt: 0
  }
};
/** A property of the annotation that is edited in place, with its name above it */
function PropertyField(t0) {
  const $ = _c(10);
  const {
    title,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      minWidth: 0
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      color: "text.secondary",
      mb: 0.5
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== title) {
    t3 = <Text variant="heading12" component="div" sx={t2}>{title}</Text>;
    $[2] = title;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      height: "2rem"
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== children) {
    t5 = <Box sx={t4}>{children}</Box>;
    $[5] = children;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t3 || $[8] !== t5) {
    t6 = <Box sx={t1}>{t3}{t5}</Box>;
    $[7] = t3;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}

/**
 * Details of an annotation: what needs doing first, then how it looks, and what it is about next
 * to who worked on it when there is room.
 */
export function MarkupsSidePanelInfo(t0) {
  const $ = _c(38);
  const {
    markup,
    preview,
    isWide: t1,
    isInScan: t2
  } = t0;
  const isWide = t1 === undefined ? false : t1;
  const isInScan = t2 === undefined ? false : t2;
  const userSettings = useAppSelector(userSettingsSelector);
  const unitMeasurement = userSettings.unit_measurement?.value ?? "meters";
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(9rem, 1fr))",
      gap: 1.5,
      mt: 2,
      p: 1.5,
      borderRadius: 1,
      backgroundColor: "action.hover"
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] !== markup) {
    t4 = <PropertyField title="Status"><MarkupStatusDropdown markup={markup} isSidePanelOpen={true} /></PropertyField>;
    $[1] = markup;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== markup) {
    t5 = <PropertyField title="Assignee"><MarkupAssignee markup={markup} isSidePanelOpen={true} /></PropertyField>;
    $[3] = markup;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== markup) {
    t6 = <PropertyField title="Due Date"><MarkupDueDate markup={markup} isSidePanelOpen={true} /></PropertyField>;
    $[5] = markup;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t4 || $[8] !== t5 || $[9] !== t6) {
    t7 = <Box sx={t3}>{t4}{t5}{t6}</Box>;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      mt: 2,
      "&:empty": {
        display: "none"
      },
      "& > :first-child": {
        mt: 0
      }
    };
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== preview) {
    t9 = <Box sx={t8}>{preview}</Box>;
    $[12] = preview;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const t10 = isWide ? "minmax(0, 3fr) minmax(0, 2fr)" : "minmax(0, 1fr)";
  let t11;
  if ($[14] !== t10) {
    t11 = {
      display: "grid",
      gridTemplateColumns: t10,
      columnGap: 4,
      rowGap: 2,
      alignItems: "start",
      mt: 2
    };
    $[14] = t10;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== markup) {
    t12 = <Box sx={COLUMN_SX}><MarkupDescription markup={markup} /></Box>;
    $[16] = markup;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== markup) {
    t13 = <MarkupSidePanelActivity markup={markup} />;
    $[18] = markup;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== markup.measurePolygon || $[21] !== unitMeasurement) {
    t14 = markup.measurePolygon && <MarkupMeasurements measurePolygon={markup.measurePolygon} unitMeasurement={unitMeasurement} />;
    $[20] = markup.measurePolygon;
    $[21] = unitMeasurement;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== isInScan || $[24] !== markup) {
    t15 = isInScan && !markup.measurePolygon && <MarkupPosition markup={markup} />;
    $[23] = isInScan;
    $[24] = markup;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t13 || $[27] !== t14 || $[28] !== t15) {
    t16 = <Box sx={COLUMN_SX}>{t13}{t14}{t15}</Box>;
    $[26] = t13;
    $[27] = t14;
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== t11 || $[31] !== t12 || $[32] !== t16) {
    t17 = <Box sx={t11}>{t12}{t16}</Box>;
    $[30] = t11;
    $[31] = t12;
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== t17 || $[35] !== t7 || $[36] !== t9) {
    t18 = <>{t7}{t9}{t17}</>;
    $[34] = t17;
    $[35] = t7;
    $[36] = t9;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  return t18;
}